/**
 * Background to a decision or an action: a file uploaded into the shared
 * drive, a Drive link or a web link. Added at any time; removed only while
 * the decision is still undecided or the action open.
 */
import { z } from "zod";
import type { DecisionStatus } from "./decisions";

export type AttachmentEntity = "decision" | "action";
export type AttachmentKind = "upload" | "drive" | "link";

export interface Attachment {
  id: string;
  entity: AttachmentEntity;
  entityId: string;
  kind: AttachmentKind;
  name: string;
  url: string;
  mimeType: string | null;
  size: number | null;
  addedBy: string | null;
  addedByName: string | null;
  addedAt: string;
}

export interface AttachmentList {
  items: Attachment[];
  /** Uploading needs Drive connected to a shared drive. */
  canUpload: boolean;
  /** Whether anything on it can still be removed. */
  canRemove: boolean;
}

/** Largest upload, in bytes. */
export const MAX_ATTACHMENT = 25 * 1024 * 1024;

export const attachmentLinkSchema = z.object({
  url: z
    .string()
    .trim()
    .max(2000)
    .refine((u) => /^https?:\/\/[^\s]+$/i.test(u), "Paste a web address starting https://"),
  name: z.string().trim().max(200).default(""),
});

export const isDriveUrl = (url: string) => /^https?:\/\/(drive|docs|sheets|slides)\.google\.com\//i.test(url);

/** A name for a link given none: the host and the last part of its path. */
export function linkName(url: string): string {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (isDriveUrl(url)) {
      const kind = u.hostname.startsWith("docs") ? (u.pathname.startsWith("/spreadsheets") ? "Sheet" : u.pathname.startsWith("/presentation") ? "Slides" : "Doc") : "Drive file";
      return `Google ${kind}`;
    }
    const last = decodeURIComponent(u.pathname.split("/").filter(Boolean).at(-1) ?? "");
    return last ? `${host} · ${last}` : host;
  } catch {
    return url;
  }
}

/** Removing is for things still being worked out. */
export const attachmentsLocked = (entity: AttachmentEntity, status: DecisionStatus | "open" | "done") => (entity === "decision" ? !["draft", "proposed", "awaiting_consent"].includes(status) : status !== "open");
